import { Injectable } from '@angular/core';
import { RagChunk, RagSearchResult } from '../models/rag.models';

@Injectable({ providedIn: 'root' })
export class Bm25Service {
  private readonly k1 = 1.5;
  private readonly b = 0.75;

  search(query: string, chunks: RagChunk[], topK: number): RagSearchResult[] {
    const queryTerms = this.tokenize(query);
    if (!queryTerms.length || !chunks.length) return [];

    const documents = chunks.map((chunk) => this.tokenize(chunk.content));
    const documentFrequency = new Map<string, number>();
    for (const terms of documents) {
      for (const term of new Set(terms)) {
        documentFrequency.set(term, (documentFrequency.get(term) ?? 0) + 1);
      }
    }

    const averageLength = documents.reduce((total, terms) => total + terms.length, 0) / documents.length;
    const totalDocuments = chunks.length;
    return chunks
      .map((chunk, index) => ({
        chunk,
        score: this.score(queryTerms, documents[index], documentFrequency, totalDocuments, averageLength),
      }))
      .filter((result) => result.score > 0)
      .sort((left, right) => right.score - left.score)
      .slice(0, topK);
  }

  tokenize(text: string): string[] {
    return text.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
  }

  private score(queryTerms: string[], terms: string[], frequencies: Map<string, number>, totalDocuments: number, averageLength: number): number {
    const termFrequency = new Map<string, number>();
    for (const term of terms) termFrequency.set(term, (termFrequency.get(term) ?? 0) + 1);

    return queryTerms.reduce((score, term) => {
      const frequency = termFrequency.get(term) ?? 0;
      const documentFrequency = frequencies.get(term) ?? 0;
      if (!frequency || !documentFrequency) return score;
      const idf = Math.log(1 + (totalDocuments - documentFrequency + 0.5) / (documentFrequency + 0.5));
      const denominator = frequency + this.k1 * (1 - this.b + this.b * (terms.length / averageLength));
      return score + idf * ((frequency * (this.k1 + 1)) / denominator);
    }, 0);
  }

  /**
   * Compute combined BM25 and TF‑IDF scores for each chunk.
   * @param query The search query string.
   * @param chunks Array of RagChunk to score.
   * @param topK Number of results to return.
   * @param alpha Weighting factor between BM25 (α) and TF‑IDF (1‑α).
   */
  searchWithTfIdf(query: string, chunks: RagChunk[], topK: number, alpha: number = 0.5): RagSearchResult[] {
    const queryTerms = this.tokenize(query);
    if (!queryTerms.length || !chunks.length) return [];

    // Tokenize all documents and build document frequency map
    const documents = chunks.map((chunk) => this.tokenize(chunk.content));
    const documentFrequency = new Map<string, number>();
    for (const terms of documents) {
      for (const term of new Set(terms)) {
        documentFrequency.set(term, (documentFrequency.get(term) ?? 0) + 1);
      }
    }

    const averageLength = documents.reduce((total, terms) => total + terms.length, 0) / documents.length;
    const totalDocuments = chunks.length;

    // Helper to compute TF‑IDF score for a single document
    const tfidfScoreForDoc = (terms: string[]): number => {
      const termFreqMap = new Map<string, number>();
      for (const t of terms) termFreqMap.set(t, (termFreqMap.get(t) ?? 0) + 1);
      let score = 0;
      for (const qTerm of queryTerms) {
        const tf = termFreqMap.get(qTerm) ?? 0;
        if (!tf) continue;
        const df = documentFrequency.get(qTerm) ?? 0;
        const idf = Math.log(1 + (totalDocuments - df + 0.5) / (df + 0.5));
        score += tf * idf;
      }
      return score;
    };

    return chunks
      .map((chunk, index) => {
        const bm25 = this.score(queryTerms, documents[index], documentFrequency, totalDocuments, averageLength);
        const tfidf = tfidfScoreForDoc(documents[index]);
        const finalScore = alpha * bm25 + (1 - alpha) * tfidf;
        return { chunk, score: finalScore } as RagSearchResult;
      })
      .filter((result) => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, topK);
  }

  /**
   * Extract top TF-IDF keywords from a document chunk, filtering out common stop words and extracting meaningful phrases
   * @param content The text content to analyze
   * @param topK Number of top keywords to return (default: 50)
   * @returns Array of top meaningful keywords/phrases sorted by frequency
   */
  extractTopKeywords(content: string, topK: number = 50): string[] {
    const terms = this.tokenize(content);
    if (!terms.length) return [];
    
    // Common English stop words to filter out completely
    const stopWords = new Set([
      'the', 'of', 'or', 'and', 'to', 'in', 'a', 'for', 'is', 'are', 'be', 'as', 'by', 'which', 'that', 'than', 'such', 'this', 'from', 'at', 'into', 'use', 'an', 'may', 'shall', 'note', 'materials', 'exchange', 'process', 'gas', 'nuclear', 'equipment', 'separation', 'agency', 'systems', 'prepared', 'uranium', 'designed', 'with', 'especially', '2', '1', '3', '4', '5', '6', 'but', 'nor', 'so', 'yet', 'for', 'if', 'when', 'where', 'why', 'how', 'what', 'which', 'who', 'whom', 'whose', 'that', 'these', 'those', 'all', 'any', 'both', 'each', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'own', 'same', 'too', 'very', 'just', 'now'
    ]);
    
    // Additional words that are often part of prepositions or connectors but not meaningful on their own
    const weakWords = new Set(['have', 'has', 'had', 'been', 'being', 'were', 'was', 'will', 'would', 'could', 'should', 'must', 'can', 'do', 'does', 'did', 'would', 'could', 'should', 'may', 'might', 'shall', 'should', 'ought', 'must']);
    
    // Extract n-grams (1-4 words) to capture phrases
    const nGrams = new Map<string, number>();
    const maxN = 4;
    
    for (let n = 1; n <= maxN; n++) {
      for (let i = 0; i <= terms.length - n; i++) {
        const phrase = terms.slice(i, i + n).join(' ');
        // Skip very short phrases or phrases with only numbers
        if (phrase.length < 4 || /^\d+$/.test(phrase)) continue;
        
        // Filter out meaningless combinations like "of the", "to the", etc.
        const trimmedPhrase = phrase.trim().toLowerCase();
        if (/^(of|to|in|for|with|by|from|on|at|as|but|or|nor|so|yet|for|and|if|when|where|why|how|what|which|who|whom|whose|that|this|these|those)\s+(the|of|to|in|for|with|by|from|on|at|as|but|or|nor|so|yet|for|and|if|when|where|why|how|what|which|who|whom|whose|that|this|these|those)$/.test(trimmedPhrase)) {
          continue;
        }
        
        // Check if phrase contains mostly meaningful words
        const wordsInPhrase = phrase.split(' ');
        const meaningfulWords = wordsInPhrase.filter(word => !stopWords.has(word) && !weakWords.has(word) && word.length > 2);
        
        // Only include phrases where at least 60% of words are meaningful (or all words if less than 3)
        const threshold = Math.max(1, Math.floor(wordsInPhrase.length * 0.6));
        if (meaningfulWords.length >= threshold || wordsInPhrase.length <= 2) {
          // Filter out phrases that are just prepositions or connectors
          if (!/^(of|to|in|for|with|by|from|on|at|as|but|or|nor|so|yet|for|and|but|if|when|where|why|how|what|which|who|whom|whose|that|this|these|those)$/.test(trimmedPhrase)) {
            // Make sure it's not just a single number or very short phrase
            if (phrase.length > 3 && !/^\d+$/.test(phrase)) {
              nGrams.set(phrase, (nGrams.get(phrase) ?? 0) + 1);
            }
          }
        }
      }
    }
    
    // For simplicity, we'll use frequency as a proxy for importance
    const phraseScores: { phrase: string; score: number }[] = [];
    for (const [phrase, frequency] of nGrams.entries()) {
      phraseScores.push({ phrase, score: frequency });
    }

    // Deduplicate phrases that are substrings of longer ones
    const uniquePhrases = new Map<string, number>();
    for (const { phrase, score } of phraseScores) {
      let isSubphrase = false;
      for (const existing of uniquePhrases.keys()) {
        if (existing.includes(phrase)) {
          // Existing longer phrase already present; skip this shorter one
          isSubphrase = true;
          break;
        }
        if (phrase.includes(existing)) {
          // Current phrase contains an existing shorter phrase; replace it
          uniquePhrases.delete(existing);
          uniquePhrases.set(phrase, score);
          isSubphrase = true;
          break;
        }
      }
      if (!isSubphrase) {
        uniquePhrases.set(phrase, score);
      }
    }

    // Convert back to array and sort by score
    const deduped = Array.from(uniquePhrases.entries()).map(([p, s]) => ({ phrase: p, score: s }));

    // Domain‑specific words that should be considered meaningful even if they appear in stopWords
    // Domain‑specific words that are meaningful even if they appear in stopWords
    const domainWords = new Set([
      'agency', 'nuclear', 'material', 'uranium', 'systems', 'process', 'equipment',
      'note', 'materials', 'exchange', 'article', 'separation', 'enrichment', 'water',
      'explanatory', 'resistant', 'product', 'protocol', 'fuel', 'high', 'conversion',
      'hydrogen', 'components', 'liquid', 'location', 'made'
    ]);

    const meaningful = deduped.filter(({ phrase }) => {
      const words = phrase.split(' ');
      // Count words that are not stop or weak
      const meaningfulCount = words.reduce((cnt, w) => cnt + (!stopWords.has(w) && !weakWords.has(w) ? 1 : 0), 0);
      if (words.length === 1) {
        return meaningfulCount > 0;
      }
      // Require at least one domain word or a decent ratio of meaningful words
      const hasDomain = words.some(w => domainWords.has(w));
      const ratio = meaningfulCount / words.length;
      if (hasDomain) return true;
      // Require a higher ratio of meaningful words to filter out generic phrases
      return ratio >= 0.6 && meaningfulCount > 0;
    });

    // Remove overlapping phrases: keep longer ones when one contains another
    const dedupedOverlaps: { phrase: string; score: number }[] = [];
    for (const { phrase, score } of meaningful) {
      let skip = false;
      for (const existing of dedupedOverlaps) {
        if (existing.phrase.includes(phrase)) {
          // Existing longer phrase already present; skip this shorter one
          skip = true;
          break;
        }
        if (phrase.includes(existing.phrase)) {
          // Current phrase is longer, replace existing
          const idx = dedupedOverlaps.indexOf(existing);
          dedupedOverlaps.splice(idx, 1, { phrase, score });
          skip = true;
          break;
        }
      }
      if (!skip) {
        dedupedOverlaps.push({ phrase, score });
      }
    }

    return dedupedOverlaps
      .sort((a, b) => b.score - a.score)
      .slice(0, Math.min(topK, dedupedOverlaps.length))
      .map(item => item.phrase);
  }
}
